// THE SHAPES of a staff bug report (plan 101 slice 05, #5427): what a reporter hands in,
// what the checkup hands back, and the row every read returns. snake_case because each is
// BOTH the MCP wire shape and the stored jsonb — one spelling, never a mapping between two.
// Split out of `../platform-bug.ts`, which is the barrel over this directory.
//
// UNTRUSTED BY CONSTRUCTION. `title` and `body` are raw text whoever filed them — a staff
// token can leak, and a body pastes tenant logs and error strings. The store keeps
// the body raw, bounded and NEVER rendered; any reader that puts it in a prompt goes
// through `quoteUntrusted(redactString(...))` (`value-objects/intake-screen-fence.ts`),
// the one fence. Reporter `evidence` is a CLAIM to verify, never a fact.

import { z } from 'zod';
import {
  AccountIdSchema,
  PlatformBugIdSchema,
  RawTaskIdSchema,
  SessionIdSchema,
  TaskIdSchema,
} from '../../value-objects/ids';
import { ScreenDiscardReasonSchema } from '../../value-objects/intake-screen';
import { HttpUrlSchema } from '../raw-task/evidence';
import {
  PLATFORM_BUG_BODY_MAX,
  PLATFORM_BUG_CONFIDENCES,
  PLATFORM_BUG_EVIDENCE_CHECKED_ITEM_MAX,
  PLATFORM_BUG_EVIDENCE_ID_MAX,
  PLATFORM_BUG_EVIDENCE_MAX_ITEMS,
  PLATFORM_BUG_PROSE_MAX,
  PLATFORM_BUG_REPRO_MAX,
  PLATFORM_BUG_TITLE_MAX,
  PlatformBugClaimSchema,
  PlatformBugSourceSchema,
  type PlatformBugStatus,
  PlatformBugStatusSchema,
  PlatformBugWontFixReasonSchema,
} from './vocab';

/** Non-blank text of at most `max` characters. Whitespace-only is not a report. */
const prose = (max: number) =>
  z
    .string()
    .max(max)
    .refine((v) => v.trim().length > 0, 'must not be blank');

/**
 * A reporter-supplied URL: well-formed `https` only. It is a claim the checkup verifies,
 * and the checkup follows nothing but our own hosts over TLS, so a plain-`http` or
 * scheme-less string is refused at the inlet rather than stored as model-read text.
 */
const HttpsUrlSchema = HttpUrlSchema.refine(
  (v) => v.startsWith('https://'),
  'expected an https URL',
);

/**
 * A reporter-supplied id, bounded AND in the house shape (`ses_…` / `tsk_…`): the id
 * brands' pattern has no upper length, and a free string here is 40 items of untrusted
 * text reaching the checkup's prompt unfenced (PR #5433 review).
 */
const SessionEvidenceIdSchema = z.string().max(PLATFORM_BUG_EVIDENCE_ID_MAX).pipe(SessionIdSchema);
const TaskEvidenceIdSchema = z.string().max(PLATFORM_BUG_EVIDENCE_ID_MAX).pipe(TaskIdSchema);

/** Reporter-supplied pointers. Claims to verify: the checkup follows only our own hosts. */
export const PlatformBugEvidenceSchema = z.strictObject({
  urls: z.array(HttpsUrlSchema).max(PLATFORM_BUG_EVIDENCE_MAX_ITEMS).default([]),
  session_ids: z.array(SessionEvidenceIdSchema).max(PLATFORM_BUG_EVIDENCE_MAX_ITEMS).default([]),
  task_ids: z.array(TaskEvidenceIdSchema).max(PLATFORM_BUG_EVIDENCE_MAX_ITEMS).default([]),
});
export type PlatformBugEvidence = z.infer<typeof PlatformBugEvidenceSchema>;

/** What `bug report` takes. */
export const PlatformBugReportSchema = z.strictObject({
  title: prose(PLATFORM_BUG_TITLE_MAX),
  body: prose(PLATFORM_BUG_BODY_MAX),
  evidence: PlatformBugEvidenceSchema.default({ urls: [], session_ids: [], task_ids: [] }),
});
export type PlatformBugReport = z.infer<typeof PlatformBugReportSchema>;
export type PlatformBugReportInput = z.input<typeof PlatformBugReportSchema>;

/**
 * The checkup's normalized RESTATEMENT of a report — the only form a bug reaches triage
 * in. The original `body` is never edited. `evidence_checked` is what the checkup
 * reproduced ITSELF (code, telemetry, GlitchTip), and `claim` answers "is it true?".
 */
export const PlatformBugCleanSchema = z.strictObject({
  problem: prose(PLATFORM_BUG_PROSE_MAX),
  repro: prose(PLATFORM_BUG_REPRO_MAX),
  expected: prose(PLATFORM_BUG_PROSE_MAX),
  evidence_checked: z
    .array(z.string().min(1).max(PLATFORM_BUG_EVIDENCE_CHECKED_ITEM_MAX))
    .max(PLATFORM_BUG_EVIDENCE_MAX_ITEMS),
  confidence: z.enum(PLATFORM_BUG_CONFIDENCES),
  claim: PlatformBugClaimSchema,
});
export type PlatformBugClean = z.infer<typeof PlatformBugCleanSchema>;

/** A commit sha, abbreviated or full, lowercase hex. */
const ShaSchema = z.string().regex(/^[0-9a-f]{7,40}$/, 'expected a 7..40 char lowercase hex sha');

/** The per-status `resolution` object, the status being its discriminator. */
export const PLATFORM_BUG_RESOLUTION_SCHEMAS = {
  needs_info: z.strictObject({ missing: prose(PLATFORM_BUG_PROSE_MAX) }),
  in_progress: z.strictObject({ note: prose(PLATFORM_BUG_PROSE_MAX) }),
  fixed: z
    .strictObject({
      pr_url: HttpsUrlSchema.optional(),
      sha: ShaSchema.optional(),
      note: prose(PLATFORM_BUG_PROSE_MAX).optional(),
    })
    .refine((r) => r.pr_url !== undefined || r.sha !== undefined, {
      message: '`fixed` requires `pr_url` or `sha` — name the change that fixed it',
    }),
  wont_fix: z.strictObject({
    reason: PlatformBugWontFixReasonSchema,
    note: prose(PLATFORM_BUG_PROSE_MAX),
  }),
  duplicate: z.strictObject({ duplicate_of: PlatformBugIdSchema }),
  /** Written by the SCREEN, never by a verdict: why the report was refused, under which prompt. */
  discarded: z.strictObject({
    reason: ScreenDiscardReasonSchema,
    prompt_version: z.string().min(1),
  }),
} as const;
export type PlatformBugDiscardResolution = z.infer<
  (typeof PLATFORM_BUG_RESOLUTION_SCHEMAS)['discarded']
>;

const rawTaskId = RawTaskIdSchema.optional();

/**
 * What `bug verdict` sets. Every arm answers "is the report true?" consistently:
 *
 *   - `in_progress` / `fixed` REQUIRE `clean` with `claim: 'confirmed'` (reproduced);
 *   - `wont_fix` with `reason: 'refuted'` REQUIRES `clean` with `claim: 'refuted'`;
 *   - `needs_info` takes `clean` optionally, and only with `claim: 'unverifiable'`;
 *   - `duplicate` names the bug it duplicates.
 *
 * `platform_bugs`' CHECKs restate every rule (`schema/platform-bugs.ts`), so a writer that
 * skipped this parse is refused by the database too.
 */
export const PlatformBugVerdictSchema = z
  .discriminatedUnion('status', [
    z.strictObject({
      status: z.literal('needs_info'),
      resolution: PLATFORM_BUG_RESOLUTION_SCHEMAS.needs_info,
      clean: PlatformBugCleanSchema.optional(),
      raw_task_id: rawTaskId,
    }),
    z.strictObject({
      status: z.literal('in_progress'),
      resolution: PLATFORM_BUG_RESOLUTION_SCHEMAS.in_progress,
      clean: PlatformBugCleanSchema,
      raw_task_id: rawTaskId,
    }),
    z.strictObject({
      status: z.literal('fixed'),
      resolution: PLATFORM_BUG_RESOLUTION_SCHEMAS.fixed,
      clean: PlatformBugCleanSchema,
      raw_task_id: rawTaskId,
    }),
    z.strictObject({
      status: z.literal('wont_fix'),
      resolution: PLATFORM_BUG_RESOLUTION_SCHEMAS.wont_fix,
      clean: PlatformBugCleanSchema.optional(),
      raw_task_id: rawTaskId,
    }),
    z.strictObject({
      status: z.literal('duplicate'),
      resolution: PLATFORM_BUG_RESOLUTION_SCHEMAS.duplicate,
      clean: PlatformBugCleanSchema.optional(),
      raw_task_id: rawTaskId,
    }),
  ])
  .superRefine((v, ctx) => {
    const want = claimRequiredFor(v);
    if (want === null) return;
    if (v.clean === undefined) {
      if (want.required) {
        ctx.addIssue({
          code: 'custom',
          path: ['clean'],
          message: `\`${v.status}\` requires \`clean\` with claim '${want.claim}' — say whether the report is true, with the evidence you checked`,
        });
      }
      return;
    }
    if (v.clean.claim !== want.claim) {
      ctx.addIssue({
        code: 'custom',
        path: ['clean', 'claim'],
        message: `\`${v.status}\` requires claim '${want.claim}', got '${v.clean.claim}'`,
      });
    }
  });
export type PlatformBugVerdict = z.infer<typeof PlatformBugVerdictSchema>;
export type PlatformBugVerdictInput = z.input<typeof PlatformBugVerdictSchema>;

/** The claim a verdict arm pins, and whether `clean` is mandatory; `null` = unconstrained. */
function claimRequiredFor(
  v: PlatformBugVerdict,
): { claim: PlatformBugClean['claim']; required: boolean } | null {
  switch (v.status) {
    case 'in_progress':
    case 'fixed':
      return { claim: 'confirmed', required: true };
    case 'needs_info':
      return { claim: 'unverifiable', required: false };
    case 'wont_fix':
      return v.resolution.reason === 'refuted' ? { claim: 'refuted', required: true } : null;
    case 'duplicate':
      return null;
  }
}

/**
 * Parse a stored `resolution` against its row's status. `screening` / `open` carry none;
 * every other status carries its arm's object. THROWS on drift, naming the status — a row
 * the CHECK admitted but the domain cannot read is refused at the read seam.
 */
export function parsePlatformBugResolution(
  status: PlatformBugStatus,
  value: unknown,
): Record<string, unknown> | null {
  if (status === 'screening' || status === 'open') {
    if (value !== null) {
      throw new Error(`platform bug in status '${status}' carries a resolution; it must be null`);
    }
    return null;
  }
  return PLATFORM_BUG_RESOLUTION_SCHEMAS[status].parse(value) as Record<string, unknown>;
}

/** One report, as every read returns it. */
export const PlatformBugSchema = z
  .object({
    id: PlatformBugIdSchema,
    source: PlatformBugSourceSchema,
    status: PlatformBugStatusSchema,
    /** UNTRUSTED raw text — see the module header. */
    title: z.string(),
    /** UNTRUSTED raw text, never rendered — see the module header. */
    body: z.string(),
    evidence: PlatformBugEvidenceSchema,
    clean: PlatformBugCleanSchema.nullable(),
    resolution: z.record(z.string(), z.unknown()).nullable(),
    rawTaskId: RawTaskIdSchema.nullable(),
    /** The account that FILED it, and the one whose BYOK key screens it — one meaning (see the column). */
    reporterAccountId: AccountIdSchema,
    /** `users.id` of the staff human who filed it. */
    reporterUserId: z.string().min(1),
    /** `api_tokens.id` when a PAT filed it; null for a session. */
    reporterTokenId: z.string().nullable(),
    /** Screen attempts that could not answer. The worker escalates at its bound. */
    screenFailures: z.number().int().min(0),
    createdAt: z.date(),
    updatedAt: z.date(),
    lastTriagedAt: z.date().nullable(),
  })
  .superRefine((row, ctx) => {
    try {
      parsePlatformBugResolution(row.status, row.resolution);
    } catch (err) {
      ctx.addIssue({
        code: 'custom',
        path: ['resolution'],
        message: err instanceof Error ? err.message : String(err),
      });
    }
  });
export type PlatformBug = z.infer<typeof PlatformBugSchema>;
